import { useState } from "react";
import { ArrowRight, MapPinned, Mountain, Zap } from "lucide-react";
import BremaLogo from "@/components/BremaLogo";
import LangToggle from "@/components/LangToggle";
import { useI18n } from "@/lib/i18n";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const [loading, setLoading] = useState(false);
  const { t } = useI18n();

  const onLogin = () => {
    setLoading(true);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1590513541311-9900d9c608c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW91bnRhaW4lMjB0cmFpbHxlbnwwfHx8fDE3ODM0OTgwODB8MA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(0.4) contrast(1.1)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-brema-obsidian/70 to-brema-obsidian" />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-4">
          <BremaLogo height={60} />
          <div className="leading-none border-l border-white/15 pl-4">
            <div className="font-display font-black text-white text-xl uppercase tracking-[0.2em]">
              ValliRider
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <LangToggle />
          <div className="font-mono text-[10px] text-zinc-200 uppercase tracking-[0.3em] hidden md:block">
            Heritage · Technical
          </div>
        </div>
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-8 pt-16 pb-32 grid lg:grid-cols-2 gap-16 items-center">
        <div className="animate-fade-up">
          <div className="text-center mb-8">
            <span
              className="font-display uppercase tracking-widest text-brema-yellow"
              data-testid="landing-tagline"
              style={{ fontSize: "1rem", fontWeight: 400 }}
            >
              {t("tagline")}
            </span>
          </div>
          <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9]">
            {t("heading1a")}
            <br />
            <span className="text-brema-yellow">{t("heading1b")}</span>.
            <br />
            <span className="text-white/80">
              {t("heading2a")} <span className="text-brema-red">{t("heading2b")}</span>.
            </span>
          </h1>
          <p className="mt-8 max-w-lg text-zinc-100 text-base leading-relaxed">
            {t("landingDesc")}
          </p>

          <div className="mt-10 flex items-center gap-4">
            <button
              onClick={onLogin}
              disabled={loading}
              data-testid="landing-login-btn"
              className="group relative bg-brema-red hover:bg-[#D90429] disabled:opacity-50 text-white font-display font-bold uppercase tracking-widest px-8 py-4 flex items-center gap-3 transition-colors"
            >
              <span className="corner-tick tl" />
              <span className="corner-tick tr" />
              <span className="corner-tick bl" />
              <span className="corner-tick br" />
              {loading ? t("redirecting") : t("enterRoadbook")}
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <div className="hidden sm:block font-mono text-[10px] text-zinc-200 uppercase tracking-[0.25em] max-w-[180px] leading-relaxed">
              {t("signInWith")}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-up">
          <FeatureCard
            icon={<Mountain size={20} />}
            title={t("featLoopTitle")}
            body={t("featLoopDesc")}
          />
          <FeatureCard
            icon={<Zap size={20} />}
            title={t("featBikeTitle")}
            body={t("featBikeDesc")}
            accent
          />
          <FeatureCard
            icon={<MapPinned size={20} />}
            title={t("featOffroadTitle")}
            body={t("featOffroadDesc")}
          />
          <FeatureCard
            icon={
              <span className="font-mono font-bold">Km</span>
            }
            title={t("featDataTitle")}
            body={t("featDataDesc")}
            accent
          />
        </div>
      </main>
    </div>
  );
}

function FeatureCard({ icon, title, body, accent }) {
  return (
    <div
      className={`relative bg-brema-obsidian/70 backdrop-blur border p-6 ${
        accent ? "border-brema-red/60" : "border-brema-yellow/60"
      }`}
    >
      <span className="corner-tick tl" />
      <span className="corner-tick br" />
      <div
        className={`w-10 h-10 flex items-center justify-center border ${
          accent ? "border-brema-red text-brema-red" : "border-brema-yellow text-brema-yellow"
        }`}
      >
        {icon}
      </div>
      <div className="mt-4 font-display font-bold uppercase tracking-wider text-white text-lg">
        {title}
      </div>
      <p className="mt-2 text-sm text-zinc-100 leading-relaxed">{body}</p>
    </div>
  );
}
